import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/Auth/authContext';
import { useI18n } from '../contexts/I18nContext';
import { useClientAccess } from '../hooks/useClientAccess';
import { statusService, SystemStatus, SystemStatusSummary } from '../services/statusService';
import { safeStorage } from '../utils/safeStorage';
import { isAbortError } from '../utils/abortError';
import { DeviceTypePie } from '../components/common/DeviceTypePie';
import SearchableSelect from '../components/common/SearchableSelect';
import { groupClientStats } from '../components/dashboard/clientGroups';
import { ClientsBarChart } from '../components/dashboard/ClientsBarChart';
import { ClientsStatsTable } from '../components/dashboard/ClientsStatsTable';
import { deviceTypeColor, deviceTypeLabel, percentOf } from '../components/dashboard/deviceTypes';
import '../styles/dashboard.css';

/**
 * The fleet at a glance: four figures (fleet, clients, up to date, waiting
 * for a refresh), the split by gateway type, and for ANEP staff the
 * biggest clients as bars plus every client in a table.
 *
 * Data: the summary endpoint first (fast, no per-client rows), then for
 * multi-client viewers the full payload in the background; the page never
 * waits for the per-client stats to paint.
 */
export const DashboardPage: React.FC = () => {
  const { isAuthenticated, loading: authLoading, user } = useAuth();
  const { shouldShowClientUI, isAnepStaff, allowedClientIds } = useClientAccess();
  // A head office (a client account whose scope spans its agencies) reads
  // the per-client rows as per-agency rows: same data, agency wording.
  const headOffice = !isAnepStaff && (allowedClientIds ?? []).length > 1;
  const showClients = shouldShowClientUI || headOffice;
  const { formatDateTime, formatTime, formatNumber, t } = useI18n();
  const [status, setStatus] = useState<SystemStatus | SystemStatusSummary | null>(null);
  const [clientsStatsLoading, setClientsStatsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  // The client the table is narrowed to (typed, or clicked in the bars).
  const [clientFilter, setClientFilter] = useState('');
  // The client the whole page is read for (picked by search); '' = everyone.
  const [scopeId, setScopeId] = useState('');
  const tableRef = useRef<HTMLElement>(null);
  const hasFetched = useRef(false);
  const fetchAttempts = useRef(0);

  const fetchStatus = useCallback(async (signal?: AbortSignal) => {
    const token = safeStorage.getItem('access_token');
    if (!token) return false;
    try {
      setLoading(true);
      setError(null);
      // Fast path: the summary (~200 B, cached 120 s) paints the figures at once.
      const summary = await statusService.getSystemStatusSummary({ signal });
      if (signal?.aborted) return false;
      setStatus(summary);
      setLastUpdated(new Date());
      hasFetched.current = true;
      fetchAttempts.current = 0;
      setLoading(false);
      // Slow path, multi-client viewers only: the per-client rows arrive later.
      if (showClients) {
        setClientsStatsLoading(true);
        statusService
          .getSystemStatus({ signal })
          .then((full) => { if (!signal?.aborted) setStatus(full); })
          .catch((err) => { if (!isAbortError(err)) console.error('Failed to fetch full dashboard stats:', err); })
          .finally(() => { if (!signal?.aborted) setClientsStatsLoading(false); });
      }
      return true;
    } catch (err: any) {
      if (isAbortError(err)) return false;
      console.error('Failed to fetch system status:', err);
      // A 401 right after login: the token may not be ready yet, retry a couple of times.
      if (err.message?.includes('401') || err.message?.includes('Unauthorized')) {
        fetchAttempts.current += 1;
        if (fetchAttempts.current < 3) {
          setLoading(false);
          return false;
        }
      }
      setError(t('dashboard.loadError'));
      setLoading(false);
      return false;
    }
  }, [showClients, t]);

  useEffect(() => {
    if (!isAuthenticated) {
      hasFetched.current = false;
      fetchAttempts.current = 0;
      return;
    }
    if (isAuthenticated && !authLoading && user) {
      const ac = new AbortController();
      if (!hasFetched.current) {
        const timeoutId = setTimeout(async () => {
          const success = await fetchStatus(ac.signal);
          if (!success && fetchAttempts.current > 0 && fetchAttempts.current < 3) {
            setTimeout(() => { if (!ac.signal.aborted) fetchStatus(ac.signal); }, 500);
          }
        }, 100);
        return () => { clearTimeout(timeoutId); ac.abort(); };
      }
      // Every two minutes: /v2/status is the most expensive read of the system.
      const interval = setInterval(() => fetchStatus(ac.signal), 120000);
      return () => { clearInterval(interval); ac.abort(); };
    }
  }, [isAuthenticated, authLoading, user, fetchStatus]);

  // The client picked by search: a group (root with its agencies, figures
  // added up) or one agency. Its cards and pie replace the fleet's.
  const rawStats = status ? (status as SystemStatus).clients_stats : undefined;
  // A head office reads its agencies side by side, not folded into one group:
  // its own row only stays when gateways are still attached to it.
  const clientsStats = rawStats && headOffice
    ? rawStats.filter((c) => c.parent_id || c.total_devices > 0).map((c) => ({ ...c, parent_id: null }))
    : rawStats;
  const groups = clientsStats ? groupClientStats(clientsStats) : [];
  // Only clients are searched; an agency is reached from its client's bars.
  const scopeOptions = groups
    .map((g) => ({ value: g.root.client_id, label: g.agencies.length > 0 ? `${g.root.client_name} · ${t('clients.agencies.count', { count: g.agencies.length })}` : g.root.client_name }))
    .sort((a, b) => a.label.localeCompare(b.label));
  const scopeGroup = scopeId ? groups.find((g) => g.root.client_id === scopeId) : undefined;
  const scopeParent = scopeId && !scopeGroup ? groups.find((g) => g.agencies.some((a) => a.client_id === scopeId)) : undefined;
  const scoped = scopeId ? (scopeGroup?.total ?? clientsStats?.find((c) => c.client_id === scopeId)) : undefined;
  // The picked client's agencies, one line and one bar each; the head office's own gateways as a line of their own.
  // A head office holds no gateways: only its agencies are drawn. Gateways
  // still attached to it (older data) are flagged in the Agencies card.
  const scopeAgencies = scopeGroup && scopeGroup.agencies.length > 0 ? scopeGroup.agencies.map((a) => ({ ...a, parent_id: null })) : [];

  const header = (
    <div className="page-header">
      <div className="page-title">
        <h1>{t('dashboard.title')}</h1>
        <p>{t('dashboard.subtitle')}</p>
      </div>
      {status && (
        <div className="page-actions">
          {showClients && (status as SystemStatus).clients_stats && (
            <SearchableSelect
              value={scopeId}
              onChange={setScopeId}
              options={scopeOptions}
              placeholder={t('dashboard.scopeSearch')}
              searchPlaceholder={t('dashboard.scopeSearch')}
              className="field-select dash-scope"
              data-testid="dashboard-scope"
              ariaLabel={t('dashboard.scopeSearch')}
            />
          )}
          <span className="last-updated" data-testid="dashboard-updated">{t('dashboard.lastUpdated', { time: formatTime(lastUpdated) })}</span>
          <button type="button" onClick={() => fetchStatus()} className="refresh-button" disabled={loading} data-testid="dashboard-refresh">
            {loading ? t('dashboard.refreshing') : t('dashboard.refresh')}
          </button>
        </div>
      )}
    </div>
  );

  if (authLoading || (loading && !status)) {
    return (
      <div className="page dashboard-page" aria-busy="true">
        {header}
        <div className="page-content dash-content" data-testid="dashboard-skeleton">
          <div className="dash-kpis">
            {[0, 1, 2, 3].map((i) => <div key={i} className="dash-kpi dash-skeleton-card"><span className="dash-skeleton dash-skeleton-label" /><span className="dash-skeleton dash-skeleton-value" /><span className="dash-skeleton dash-skeleton-meta" /></div>)}
          </div>
          <div className="dash-row has-two">
            <div className="dash-card dash-skeleton-card"><span className="dash-skeleton dash-skeleton-title" /><span className="dash-skeleton dash-skeleton-block" /></div>
            <div className="dash-card dash-skeleton-card"><span className="dash-skeleton dash-skeleton-title" /><span className="dash-skeleton dash-skeleton-block" /></div>
          </div>
          <p className="dash-state-hint">{authLoading ? t('dashboard.authenticating') : t('dashboard.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page dashboard-page">
        {header}
        <div className="page-content">
          <div className="dash-state dash-state-error" role="alert">
            <p>{error}</p>
            <button type="button" onClick={() => fetchStatus()} className="btn btn-primary btn-sm">{t('dashboard.retry')}</button>
          </div>
        </div>
      </div>
    );
  }

  if (!status) return null;

  const figures = scoped
    ? { total_devices: scoped.total_devices, devices_needing_refresh: scoped.devices_needing_refresh, devices_by_type: scoped.devices_by_type }
    : status;
  const scopedUpToDate = figures.total_devices - figures.devices_needing_refresh;
  const scopedUpToDatePct = percentOf(scopedUpToDate, figures.total_devices);
  const scopedRefreshPct = percentOf(figures.devices_needing_refresh, figures.total_devices);
  const deviceLink = (suffix: string) => (scoped ? `/devices?client=${encodeURIComponent(scoped.client_id)}${suffix ? `&${suffix}` : ''}` : `/devices${suffix ? `?${suffix}` : ''}`);

  return (
    <div className="page dashboard-page">
      {header}

      <div className="page-content dash-content">
        {scoped && (
          <div className="dash-scope-banner" data-testid="dashboard-scope-banner">
            <strong>{t('dashboard.scopeTitle', { name: scoped.client_name })}</strong>
            <span>{scopeParent ? t('clients.agencies.agencyOf', { name: scopeParent.root.client_name }) : t('dashboard.scopeHint')}</span>
            {scopeParent && <button type="button" className="btn btn-link btn-sm" onClick={() => setScopeId(scopeParent.root.client_id)} data-testid="dashboard-scope-parent">← {scopeParent.root.client_name}</button>}
            <button type="button" className="btn btn-link btn-sm" onClick={() => setScopeId('')} data-testid="dashboard-scope-clear">{t('dashboard.scopeAll')}</button>
          </div>
        )}

        {/* The four figures: the fleet's, or the picked client's */}
        <div className="dash-kpis" data-testid="dashboard-kpis">
          <Link to={deviceLink('')} className="dash-kpi is-fleet" title={t('dashboard.seeDevices')} data-testid="kpi-fleet">
            <span className="dash-kpi-label">{t('dashboard.kpiFleet')}</span>
            <span className="dash-kpi-value">{formatNumber(figures.total_devices)}</span>
            {!scoped && <span className="dash-kpi-meta">{t('dashboard.kpiFleetMeta', { count: formatNumber(status.recent_devices_count) })}</span>}
          </Link>

          {showClients && !scoped && (
            <Link to="/clients" className="dash-kpi is-clients" title={t(headOffice ? 'dashboard.seeAgencies' : 'dashboard.seeClients')} data-testid="kpi-clients">
              <span className="dash-kpi-label">{t(headOffice ? 'dashboard.kpiAgencies' : 'dashboard.kpiClients')}</span>
              <span className="dash-kpi-value">{formatNumber(headOffice ? Math.max(0, status.total_clients - 1) : status.total_clients)}</span>
              <span className="dash-kpi-meta">{t(headOffice ? 'dashboard.kpiAgenciesMeta' : 'dashboard.kpiClientsMeta', { count: formatNumber(headOffice ? Math.max(0, status.active_clients - 1) : status.active_clients) })}</span>
            </Link>
          )}
          {scoped && scopeGroup && scopeGroup.agencies.length > 0 && (
            <Link to="/clients" className="dash-kpi is-clients" title={t('dashboard.seeAgencies')} data-testid="kpi-clients">
              <span className="dash-kpi-label">{t('dashboard.kpiAgencies')}</span>
              <span className="dash-kpi-value">{formatNumber(scopeGroup.agencies.length)}</span>
              <span className={`dash-kpi-meta${scopeGroup.root.total_devices > 0 ? ' is-warning' : ''}`}>{scopeGroup.root.total_devices > 0 ? t('clients.agencies.unassigned', { count: formatNumber(scopeGroup.root.total_devices) }) : t('clients.agencies.allAssigned')}</span>
            </Link>
          )}

          <Link to={deviceLink('status=active')} className="dash-kpi is-uptodate" title={t('dashboard.seeDevices')} data-testid="kpi-uptodate">
            <span className="dash-kpi-label">{t('dashboard.kpiUpToDate')}</span>
            <span className="dash-kpi-value">{formatNumber(scopedUpToDate)}</span>
            <span className="dash-kpi-meta">{t('dashboard.kpiShare', { pct: scopedUpToDatePct })}</span>
            <span className="dash-kpi-track"><span className="dash-kpi-fill" style={{ width: `${figures.total_devices ? (scopedUpToDate / figures.total_devices) * 100 : 0}%` }} /></span>
          </Link>

          <Link to={deviceLink('status=refresh')} className="dash-kpi is-refresh" title={t('dashboard.seeDevices')} data-testid="kpi-refresh">
            <span className="dash-kpi-label">{t('dashboard.kpiRefresh')}</span>
            <span className="dash-kpi-value">{formatNumber(figures.devices_needing_refresh)}</span>
            <span className="dash-kpi-meta">{t('dashboard.kpiShare', { pct: scopedRefreshPct })}{!scoped && ` · ${t('dashboard.kpiRefreshMeta', { count: formatNumber(status.recent_updates_count) })}`}</span>
            <span className="dash-kpi-track"><span className="dash-kpi-fill" style={{ width: `${figures.total_devices ? (figures.devices_needing_refresh / figures.total_devices) * 100 : 0}%` }} /></span>
          </Link>
        </div>

        {/* Split by type, and the biggest clients */}
        <div className={`dash-row${(showClients && !scoped) || scopeAgencies.length > 0 ? ' has-two' : ''}`}>
          <section className="dash-card">
            <header className="dash-card-head">
              <h2>{t('dashboard.deviceTypeDistribution')}</h2>
              <p>{scoped ? t('dashboard.scopeTitle', { name: scoped.client_name }) : t('dashboard.breakdownByType')}</p>
            </header>
            <DeviceTypePie
              total={figures.total_devices}
              slices={figures.devices_by_type.map((x) => ({
                type: x.device_type,
                label: deviceTypeLabel(x.device_type, t('dashboard.unknownType')),
                count: x.count,
                color: deviceTypeColor(x.device_type),
              }))}
            />
          </section>

          {scoped && scopeAgencies.length > 0 && (
            <section className="dash-card" data-testid="dashboard-scope-bars">
              <header className="dash-card-head">
                <h2>{t('dashboard.agenciesChart')}</h2>
                <p>{t('dashboard.scopeBarsHint')}</p>
              </header>
              <ClientsBarChart clients={scopeAgencies} top={8} selectedId={null} onSelect={(c) => { if (c.client_id !== scopeGroup?.root.client_id) setScopeId(c.client_id); }} />
            </section>
          )}

          {showClients && !scoped && (
            <section className="dash-card">
              <header className="dash-card-head">
                <h2>{t(headOffice ? 'dashboard.agenciesChart' : 'dashboard.clientsChart')}</h2>
                <p>{t('dashboard.clientsChartHint', { count: 8 })}</p>
              </header>
              {clientsStats ? (
                <ClientsBarChart
                  clients={clientsStats}
                  top={8}
                  selectedId={clientsStats.find((c) => c.client_name === clientFilter)?.client_id ?? null}
                  onSelect={(c) => {
                    setClientFilter((current) => (current === c.client_name ? '' : c.client_name));
                    tableRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
                  }}
                />
              ) : (
                <div className="dash-empty">{clientsStatsLoading ? t('dashboard.tableLoading') : t('dashboard.noClientsToDisplay')}</div>
              )}
            </section>
          )}
        </div>

        {/* Every client */}
        {showClients && !scoped && (
          <section className="dash-card" ref={tableRef}>
            <header className="dash-card-head">
              <h2>{t(headOffice ? 'dashboard.agencyStatistics' : 'dashboard.clientStatistics')}</h2>
              <p>{t(headOffice ? 'dashboard.deviceBreakdownByAgency' : 'dashboard.deviceBreakdownByClient')}</p>
            </header>
            <ClientsStatsTable clients={clientsStats ?? []} loading={clientsStatsLoading} filter={clientFilter} onFilterChange={setClientFilter} />
          </section>
        )}

        {/* A picked client with agencies: its agencies, one line each */}
        {scoped && scopeAgencies.length > 0 && (
          <section className="dash-card" data-testid="dashboard-scope-agencies">
            <header className="dash-card-head">
              <h2>{t('dashboard.agencyStatistics')}</h2>
              <p>{t('dashboard.deviceBreakdownByAgency')}</p>
            </header>
            <ClientsStatsTable clients={scopeAgencies} loading={false} filter="" onFilterChange={() => undefined} />
          </section>
        )}

        <footer className="dash-foot" data-testid="dashboard-foot">
          <span>{t('dashboard.footAdded', { count: formatNumber(status.recent_devices_count) })}</span>
          <span>{t('dashboard.footUpdated', { count: formatNumber(status.recent_updates_count) })}</span>
          <span>{t('dashboard.footDataAt', { time: formatDateTime(status.timestamp) })}</span>
        </footer>
      </div>
    </div>
  );
};
